import { NotificationsDropdownMenu } from '@/components/layout/Navigation/notifications/notifications-dropdown-menu';
import { NotificationsPanel } from '@/components/layout/Navigation/notifications/notifications-panel';
import {
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
} from '@heidi/design-system/popover';
import { typographyVariant } from '@heidi/design-system/typography';
import { useCallback, useId, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  NotificationsPopoverControlContext,
  type NotificationsPopoverControl,
} from './notifications-popover-focus';

type NotificationsPopoverContentProps = {
  /**
   * Called when a descendant asks to close the popover (e.g. a card
   * action-click). The `<Popover>` Root lives in `SideNavFooter`, so
   * closing is done by lifting a `setOpen(false)` through this prop.
   */
  onRequestClose: () => void;
};

/**
 * Renders the notifications panel as the content of a Radix Popover.
 *
 * Intentionally **not** wrapped in its own `<Popover>` Root. The Root lives in
 * `SideNavFooter` so the bell button (rendered inside `<FooterIconButtons />`)
 * and this content share a common ancestor — that's what lets `PopoverAnchor`
 * around the bell position this content off the bell precisely, across all
 * three bell render modes (label-icons, vertical NavItem, horizontal).
 *
 * Nested overlays compose normally:
 * - The header kebab `DropdownMenu` and per-row kebab/`ContextMenu` portals are
 *   tracked by Radix's `DismissableLayer` stack, so opening them does not
 *   dismiss this popover.
 * - `onInteractOutside` adds a safety net for the `ContextMenu` case (its
 *   right-click trigger and the portal mount point are far apart in the DOM,
 *   which has historically tripped the default outside-click guard).
 */
export const NotificationsPopoverContent = ({
  onRequestClose,
}: NotificationsPopoverContentProps) => {
  const titleId = useId();
  // Ref over state — we only need the latest value inside the Radix
  // close handler and never want to re-render the popover when the flag
  // flips. `useCallback` with `[]` keeps the context value stable so
  // consumers don't re-render either.
  const skipFocusReturnRef = useRef(false);
  const skipFocusReturn = useCallback(() => {
    skipFocusReturnRef.current = true;
  }, []);

  const control = useMemo<NotificationsPopoverControl>(
    () => ({ skipFocusReturn, close: onRequestClose }),
    [skipFocusReturn, onRequestClose]
  );

  return (
    <NotificationsPopoverControlContext.Provider value={control}>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={12}
        collisionPadding={{ top: 8, bottom: 8 }}
        data-testid="notifications-popover"
        aria-labelledby={titleId}
        // `h-[374px]` gives `NotificationsPanel`'s `flex-auto min-h-0` scroller a
        // bounded parent — virtua's `VList` measures 0 rows without one.
        //
        // `data-[experimental=true]:gap-2` mirrors the DS's experimental
        // `gap-2.5` so tailwind-merge collapses the two and ours wins;
        // without the variant prefix they sit in different merge buckets and
        // `gap-2` loses to `gap-2.5` at runtime, forcing `!gap-2`.
        className="flex h-[374px] w-80 flex-col gap-2 px-2 pb-0 pt-4 data-[experimental=true]:gap-2"
        onCloseAutoFocus={(event) => {
          // Action-click path: navigation is about to happen, so don't
          // restore focus to the bell — otherwise the bell's tooltip
          // pops on the destination page. Escape / outside-click keep
          // Radix's default focus return for a11y.
          if (skipFocusReturnRef.current) {
            skipFocusReturnRef.current = false;
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          // Belt-and-braces guard: keep the popover open when the user interacts
          // with a portaled child overlay (header kebab, row kebab, row
          // ContextMenu). Radix usually handles this via layer ancestry, but
          // ContextMenu's trigger ↔ portal distance has tripped it before, so
          // we explicitly bail out for any Radix popper portal or menu item.
          const target = event.target as Element | null;
          if (
            target?.closest(
              '[data-radix-popper-content-wrapper], [role="menu"], [role="menuitem"], [data-radix-menu-content]'
            )
          ) {
            event.preventDefault();
          }
        }}
      >
        <PopoverHeader className="flex flex-row items-center justify-between gap-2 px-3">
          <PopoverTitle id={titleId} className={typographyVariant({ variant: 'p2Bold' })}>
            <FormattedMessage defaultMessage="Notifications" id="NAidKbB0vi" />
          </PopoverTitle>
          <NotificationsDropdownMenu />
        </PopoverHeader>
        <NotificationsPanel />
      </PopoverContent>
    </NotificationsPopoverControlContext.Provider>
  );
};
